import { liesBezug } from "./zellbezug";

/*
  Eine Formel zerlegen und in einen Baum bringen.

  ---------------------------------------------------------------
  Zwei Trennzeichen, die sich nicht vertragen
  ---------------------------------------------------------------

  In der deutschen Schreibweise ist das Komma das Dezimalzeichen. Damit ist
  es als Trennzeichen zwischen Argumenten verbraucht — `SUMME(1,5;2)` wäre
  sonst nicht zu entscheiden. Deshalb, wie im deutschen Excel:

      ;   trennt Argumente
      ,   trennt Ganzes und Bruch

  ---------------------------------------------------------------
  Warum Fehlerwörter zerlegbar sein müssen
  ---------------------------------------------------------------

  Wird eine Zeile gelöscht, auf die eine Formel zeigt, steht danach
  `=SUMME(#BEZUG!)` im TEXT der Zelle — so macht es jede
  Tabellenkalkulation, damit man sieht, was kaputtging.

  Ein Zerleger, der `#BEZUG!` nicht kennt, macht daraus einen Syntaxfehler:
  Aus „hier fehlt eine Zeile" würde „diese Formel ist unverständlich". Der
  Nutzer sucht dann am falschen Ende. Deshalb ist ein Fehlerwort eine
  reguläre Art von Baustein.

  ---------------------------------------------------------------
  Warum der Doppelpunkt ein Operator ist
  ---------------------------------------------------------------

  Nicht `B2:B4` als ein Baustein lesen, sondern zwei Bezüge und dazwischen
  ein Doppelpunkt. Dann fällt ein alleinstehendes `:` von selbst als Fehler
  heraus, statt in einem Bezugsmuster stillschweigend zu verschwinden.
*/

export const FEHLERWOERTER = [
  "#WERT!",
  "#DIV/0!",
  "#NAME?",
  "#BEZUG!",
  "#ZIRKEL!",
  "#FORMEL?",
];

/* Nur diese sechs. Was sonst wie eine Funktion aussieht, ergibt #NAME?. */
export const FUNKTIONSNAMEN = ["SUMME", "MITTELWERT", "ANZAHL", "MIN", "MAX", "WENN"];

export function istFormel(text) {
  return typeof text === "string" && text.trimStart().startsWith("=");
}

/* ---------------------------------------------------------------
   Zerlegen
   --------------------------------------------------------------- */

const VERGLEICHE = ["<=", ">=", "<>"];
const EINZELN = "+-*/^=<>";

/*
  Aus dem Text hinter dem Gleichheitszeichen wird eine Kette von Bausteinen.

  Jeder Baustein merkt sich seinen Text so, wie er dastand (`roh`). Nur
  deshalb kann der Verschieber später eine Formel umschreiben, ohne die
  Schreibweise des Nutzers zu zerstören — Leerzeichen, Groß- und
  Kleinschreibung bleiben, wo sie waren.
*/
export function zerlege(text) {
  const roh = istFormel(text) ? text.trimStart().slice(1) : String(text ?? "");
  const teile = [];
  let i = 0;

  while (i < roh.length) {
    const z = roh[i];

    if (z === " " || z === "\t") {
      const anfang = i;
      while (i < roh.length && (roh[i] === " " || roh[i] === "\t")) i += 1;
      teile.push({ art: "luft", roh: roh.slice(anfang, i) });
      continue;
    }

    /* Fehlerwörter vor allem anderen — sie beginnen mit einem Zeichen,
       das sonst nirgends vorkommt. */
    if (z === "#") {
      const hier = i;
      const wort = FEHLERWOERTER.find((w) => roh.startsWith(w, hier));
      if (wort) {
        teile.push({ art: "fehler", roh: wort, code: wort });
        i += wort.length;
        continue;
      }
      teile.push({ art: "unbekannt", roh: z });
      i += 1;
      continue;
    }

    if (z === '"') {
      const anfang = i;
      i += 1;
      let inhalt = "";
      let geschlossen = false;
      while (i < roh.length) {
        if (roh[i] === '"') {
          /* Zwei Anführungszeichen hintereinander sind eines im Text. */
          if (roh[i + 1] === '"') {
            inhalt += '"';
            i += 2;
            continue;
          }
          i += 1;
          geschlossen = true;
          break;
        }
        inhalt += roh[i];
        i += 1;
      }
      teile.push({
        art: geschlossen ? "text" : "unbekannt",
        roh: roh.slice(anfang, i),
        wert: inhalt,
      });
      continue;
    }

    /*
      Zahl oder Bezug? Beide können mit einer Ziffer beginnen — `2` und
      `2:3` nicht, aber `A2` schon. Deshalb erst auf einen Bezug prüfen und
      nur dann auf eine Zahl, wenn keiner passt.
    */
    if (/[A-Za-z$]/.test(z)) {
      const anfang = i;
      while (i < roh.length && /[A-Za-z0-9$]/.test(roh[i])) i += 1;
      const wort = roh.slice(anfang, i);

      const bezug = liesBezug(wort);
      if (bezug) {
        teile.push({ art: "bezug", roh: wort, bezug });
        continue;
      }

      /* Ein Name gilt nur, wenn eine Klammer folgt — sonst ist es Unsinn. */
      const dahinter = roh.slice(i).trimStart();
      teile.push({
        art: dahinter.startsWith("(") ? "name" : "unbekannt",
        roh: wort,
        name: wort.toUpperCase(),
      });
      continue;
    }

    if (/[0-9,]/.test(z)) {
      const anfang = i;
      while (i < roh.length && /[0-9.,]/.test(roh[i])) i += 1;
      const wort = roh.slice(anfang, i);
      const zahl = alsZahl(wort);
      teile.push(
        zahl === null
          ? { art: "unbekannt", roh: wort }
          : { art: "zahl", roh: wort, wert: zahl }
      );
      continue;
    }

    if (z === "(") {
      teile.push({ art: "auf", roh: z });
      i += 1;
      continue;
    }
    if (z === ")") {
      teile.push({ art: "zu", roh: z });
      i += 1;
      continue;
    }
    if (z === ";") {
      teile.push({ art: "trenner", roh: z });
      i += 1;
      continue;
    }
    if (z === ":") {
      teile.push({ art: "bis", roh: z });
      i += 1;
      continue;
    }

    const zwei = roh.slice(i, i + 2);
    if (VERGLEICHE.includes(zwei)) {
      teile.push({ art: "rechen", roh: zwei, zeichen: zwei });
      i += 2;
      continue;
    }
    if (EINZELN.includes(z)) {
      teile.push({ art: "rechen", roh: z, zeichen: z });
      i += 1;
      continue;
    }

    teile.push({ art: "unbekannt", roh: z });
    i += 1;
  }

  return teile;
}

/*
  Eine Zahl in einer Formel — hier OHNE Tausenderpunkte.

  In `zahlformat.liesZahl` sind sie erlaubt, weil ein Mensch sie eintippt.
  In einer Formel wären sie zweideutig: `SUMME(1.234)` — ein Argument oder
  eine getippte Tausend? Deshalb gilt in Formeln nur die schlichte
  Schreibweise mit Komma.
*/
function alsZahl(wort) {
  if (!/^[0-9]*,?[0-9]*$/.test(wort)) return null;
  if (wort === "" || wort === ",") return null;
  const zahl = Number(wort.replace(",", "."));
  return Number.isFinite(zahl) ? zahl : null;
}

/* ---------------------------------------------------------------
   In einen Baum bringen
   --------------------------------------------------------------- */

/*
  Rekursiver Abstieg. Die Rangfolge steckt in der Reihenfolge der
  Funktionen: Was weiter unten steht, bindet stärker.

      Vergleich   =  <>  <  >  <=  >=
      Summe       +  −
      Produkt     *  /
      Vorzeichen  −x  +x
      Potenz      ^            (rechts vor links: 2^3^2 ist 2^9)
      Grund       Zahl, Text, Bezug, Bereich, Klammer, Funktion

  Geht etwas schief, kommt ein Knoten `{ art: "fehler", code: "#FORMEL?" }`
  zurück — kein Werfen. Eine kaputte Formel ist ein normaler Zustand einer
  Tabelle, kein Ausnahmefall des Programms.
*/
export function bauBaum(teile) {
  const wichtig = teile.filter((t) => t.art !== "luft");
  const stand = { teile: wichtig, i: 0 };

  if (wichtig.length === 0) return kaputt();
  if (wichtig.some((t) => t.art === "unbekannt")) return kaputt();

  const baum = leseVergleich(stand);
  if (stand.i !== wichtig.length) return kaputt();
  return baum;
}

function kaputt() {
  return { art: "fehler", code: "#FORMEL?" };
}

function jetzt(stand) {
  return stand.teile[stand.i] || null;
}

function istRechen(teil, zeichen) {
  return teil && teil.art === "rechen" && zeichen.includes(teil.zeichen);
}

function leseVergleich(stand) {
  let links = leseSumme(stand);
  while (istRechen(jetzt(stand), ["=", "<>", "<", ">", "<=", ">="])) {
    const zeichen = stand.teile[stand.i].zeichen;
    stand.i += 1;
    const rechts = leseSumme(stand);
    links = { art: "zwei", zeichen, links, rechts };
  }
  return links;
}

function leseSumme(stand) {
  let links = leseProdukt(stand);
  while (istRechen(jetzt(stand), ["+", "-"])) {
    const zeichen = stand.teile[stand.i].zeichen;
    stand.i += 1;
    const rechts = leseProdukt(stand);
    links = { art: "zwei", zeichen, links, rechts };
  }
  return links;
}

function leseProdukt(stand) {
  let links = leseVorzeichen(stand);
  while (istRechen(jetzt(stand), ["*", "/"])) {
    const zeichen = stand.teile[stand.i].zeichen;
    stand.i += 1;
    const rechts = leseVorzeichen(stand);
    links = { art: "zwei", zeichen, links, rechts };
  }
  return links;
}

function leseVorzeichen(stand) {
  if (istRechen(jetzt(stand), ["-", "+"])) {
    const zeichen = stand.teile[stand.i].zeichen;
    stand.i += 1;
    return { art: "eins", zeichen, wert: leseVorzeichen(stand) };
  }
  return lesePotenz(stand);
}

function lesePotenz(stand) {
  const grund = leseGrund(stand);
  if (istRechen(jetzt(stand), ["^"])) {
    stand.i += 1;
    /* Rechts vor links, deshalb wieder beim Vorzeichen einsteigen. */
    return { art: "zwei", zeichen: "^", links: grund, rechts: leseVorzeichen(stand) };
  }
  return grund;
}

function leseGrund(stand) {
  const teil = jetzt(stand);
  if (!teil) return kaputt();

  if (teil.art === "zahl") {
    stand.i += 1;
    return { art: "zahl", wert: teil.wert };
  }

  if (teil.art === "text") {
    stand.i += 1;
    return { art: "text", wert: teil.wert };
  }

  if (teil.art === "fehler") {
    stand.i += 1;
    return { art: "fehler", code: teil.code };
  }

  if (teil.art === "bezug") {
    stand.i += 1;
    /* Folgt ein Doppelpunkt, ist es ein Bereich. */
    if (jetzt(stand) && jetzt(stand).art === "bis") {
      stand.i += 1;
      const zweiter = jetzt(stand);
      if (!zweiter || zweiter.art !== "bezug") return kaputt();
      stand.i += 1;
      return { art: "bereich", von: teil.bezug, bis: zweiter.bezug };
    }
    return { art: "bezug", bezug: teil.bezug };
  }

  if (teil.art === "auf") {
    stand.i += 1;
    const innen = leseVergleich(stand);
    if (!jetzt(stand) || jetzt(stand).art !== "zu") return kaputt();
    stand.i += 1;
    return innen;
  }

  if (teil.art === "name") {
    const name = teil.name;
    stand.i += 1;
    if (!jetzt(stand) || jetzt(stand).art !== "auf") return kaputt();
    stand.i += 1;

    const argumente = [];
    if (jetzt(stand) && jetzt(stand).art === "zu") {
      stand.i += 1;
      return { art: "aufruf", name, argumente };
    }

    for (;;) {
      argumente.push(leseVergleich(stand));
      const naechstes = jetzt(stand);
      if (naechstes && naechstes.art === "trenner") {
        stand.i += 1;
        continue;
      }
      if (naechstes && naechstes.art === "zu") {
        stand.i += 1;
        break;
      }
      return kaputt();
    }

    return { art: "aufruf", name, argumente };
  }

  return kaputt();
}

/*
  Alle Bezüge in einem Baum — für die Abhängigkeiten.

  Ein Bereich zählt mit seinen beiden Ecken; die Zellen dazwischen ergeben
  sich erst beim Rechnen, wenn die Größe des Blattes bekannt ist.
*/
export function bezuegeIn(knoten, sammlung = []) {
  if (!knoten || typeof knoten !== "object") return sammlung;

  if (knoten.art === "bezug") sammlung.push(knoten.bezug);
  else if (knoten.art === "bereich") sammlung.push(knoten.von, knoten.bis);
  else if (knoten.art === "zwei") {
    bezuegeIn(knoten.links, sammlung);
    bezuegeIn(knoten.rechts, sammlung);
  } else if (knoten.art === "eins") bezuegeIn(knoten.wert, sammlung);
  else if (knoten.art === "aufruf") {
    for (const a of knoten.argumente) bezuegeIn(a, sammlung);
  }

  return sammlung;
}
